const createValueLabelMap = (lookupData) => {
  return lookupData.reduce((acc, item) => {
    if (!acc[item.uniqueKey]) {
      acc[item.uniqueKey] = {};
    }
    acc[item.uniqueKey][item.value] = item.label;
    return acc;
  }, {});
};

// Maps frontend attribute names to lookup data uniqueKeys
const attributeToLookupKeyMap = {
  Minimum: 'inputType',
  'Association Type': 'associationType',
  unit: 'units',
  quantity: 'quantityRequired',
  tags: 'tags',
  fedStandardCode: 'fedStandardCode',
  criteriaCode: 'criteria',
};

const getLookupLabel = (attribute, value, lookupData) => {
  // Handle tags attribute with array values
  if (attribute === 'tags' && Array.isArray(value)) {
    // For tags, we'll assume the lookupKey is 'tags' or find an appropriate mapping
    const lookupKey = attributeToLookupKeyMap[attribute] || 'tags';
    const valueLabelMap = createValueLabelMap(lookupData);
    // Map each tag value to its label
    return value.map((tagValue) => {
      const label = valueLabelMap[lookupKey]?.[tagValue];
      return label ? `${tagValue} - ${label}` : tagValue;
    });
  }

  // Existing logic for non-tags attributes
  const lookupKey = attributeToLookupKeyMap[attribute];
  if (!lookupKey) return value;

  const valueLabelMap = createValueLabelMap(lookupData);
  const label = valueLabelMap[lookupKey]?.[value];

  // Return in "value : label" format if we have a label, otherwise just return the value
  return label ? `${value}-${label}` : value;
};

const getReadableAttribute = (attribute) => {
  if (attribute === 'fedStandardCode') return 'Federal Standard Code';
  if (attribute === 'criteriaCode') return 'Criteria';
  if (attribute === 'lowerBound') return 'Value-1';
  if (attribute === 'upperBound') return 'Value-2';
  return attribute
    .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

const capitalizeFirstChar = (str) => {
  return str && str.charAt(0).toUpperCase() + str.slice(1);
};

const TABLE_HEADER = {
  Attribute: 'Attribute',
  PreviousValue: 'Previous Value',
  NewValue: 'New Value',
};

// changeTypeMap
// Helper function to format special Minimum attribute display
const formatMinimumAttributeDisplay = (data, lookupData) => {
  if (!data?.Minimum) return '';

  const valueLabelMap = createValueLabelMap(lookupData);
  const minimumValue = data.Minimum;

  // Get labels for lookup values
  const getLabel = (key, value) => {
    const lookupKey = attributeToLookupKeyMap[key];
    return lookupKey ? valueLabelMap[lookupKey]?.[value] : null;
  };

  const minimumLabel = getLabel('Minimum', minimumValue) || minimumValue;
  const criteriaLabel = data.criteriaCode
    ? getLabel('criteriaCode', data.criteriaCode) || data.criteriaCode
    : '';
  const unitLabel = data.unit ? getLabel('unit', data.unit) || data.unit : '';

  const getNumericDisplay = () => {
    const criteriaDisplay = criteriaLabel
      ? `${data.criteriaCode}: ${criteriaLabel}`
      : '';
    const valueDisplay =
      criteriaLabel.toUpperCase() === 'ANY' ? '-' : (data.lowerBound ?? '');
    const unitDisplay = unitLabel
      ? `${data.unit}: ${unitLabel}`
      : data.unit || '';
    return `N: ${minimumLabel} ${criteriaDisplay} ${valueDisplay} ${unitDisplay}`
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getRangeDisplay = () => {
    const lowerBound = data.lowerBound || '';
    const upperBound = data.upperBound || '';
    const rangeDisplay = `${lowerBound} - ${upperBound}`;
    const unitRangeDisplay = unitLabel || data.unit || '';
    return `R: ${minimumLabel} ${rangeDisplay} ${unitRangeDisplay}`
      .trim()
      .replace(/\s+/g, ' ');
  };

  switch (minimumValue) {
    case 'P': // Predefined dropdown
      if (
        data.preDefinedValue?.value &&
        Array.isArray(data.preDefinedValue.value)
      ) {
        const values = data.preDefinedValue.value.join(', ');
        return `P: ${minimumLabel} ${values}`;
      }
      return `P: ${minimumLabel}`;

    case 'N': // Numerical
      return getNumericDisplay();

    case 'R': // Numerical range
      return getRangeDisplay();

    default:
      return `${minimumValue}: ${minimumLabel}`;
  }
};

// Helper function to format values for display
const formatValueForDisplay = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    return `<ul style="margin: 0; padding-left: 16px;">${value.map((item) => `<li>${item}</li>`).join('')}</ul>`;
  }
  return value;
};

// changeTypeMap
const CHANGE_TYPES = {
  SOLICITATION_LINE_ADDED: 'Standard item added',
  SOLICITATION_LINE_REMOVED: 'Standard item removed',
  SOLICITATION_LINE_RESTORED: 'Solicitation line restored',
  SOLICITATION_LINE_QUANTITY_CHANGED: 'Solicitation line quantity changed',
  STANDARD_ITEM_DEACTIVATED: 'Standard item deactivated',
  STANDARD_ITEM_ACTIVATED: 'Standard item activated',
  STANDARD_ITEM_REMOVED: 'Standard item removed',
  STANDARD_ITEM_ADDED: 'Standard item added',
  STANDARD_ITEM_ATTRIBUTES_UPDATED: 'Standard item attributes updated',
  EQUIPMENT_CODE_ATTRIBUTE_UPDATED: 'Equipment code attribute updated',
  EQUIPMENT_CODE_ADDED_TO_STANDARD_ITEM:
    'Equipment code added to Standard item',
  EQUIPMENT_CODE_REMOVED_FROM_STANDARD_ITEM:
    'Equipment code removed from Standard item',
  EQUIPMENT_CODE_ASSOCIATION_UPDATED:
    'Equipment code association attribute updated',
};

export {
  createValueLabelMap,
  getLookupLabel,
  capitalizeFirstChar,
  getReadableAttribute,
  TABLE_HEADER,
  CHANGE_TYPES,
  formatValueForDisplay,
  formatMinimumAttributeDisplay,
};
